import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import clsx from 'clsx';
import { $getTableCellNodeFromLexicalNode, $isTableCellNode } from '@lexical/table';
import { $getSelection, $isRangeSelection, $getRoot, KEY_DOWN_COMMAND, COMMAND_PRIORITY_EDITOR } from 'lexical';
import { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';

import type { JSX } from 'react';
import type { TextNode } from 'lexical';
import type { addTableForm } from './AddTableFrom';
import type { Actions } from './TableCellContextMenu';

import { AddTableForm } from './AddTableFrom';
import { createTable, deleteColumn, deleteRow, deleteTable, insertColumn, insertRow } from './helpers';

import { useFlyout } from 'components/RteEditor/hooks/useFlyout';
import { Icon } from 'components';
import styles from 'components/RteEditor/RteEditor.module.css';
import { useTranslation } from 'components/RteEditor/hooks/useTranslation';

const PREVENT_OPEN_DELAY = 200;

interface TablePluginProps {
  isActive: boolean;
}

export interface TableMutation {
  mutateTable: (type: Actions) => void;
}

export const TablePlugin = forwardRef(({ isActive }: TablePluginProps, ref): JSX.Element => {
  const t = useTranslation();
  const [editor] = useLexicalComposerContext();
  const { flyout, showFlyout, hideFlyout, isVisible } = useFlyout();
  const addTableForm = useRef<addTableForm>(undefined);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const preventOpen = useRef(false);
  const isVisibleRef = useRef(isVisible);

  const openFlyout = () => {
    const rect = buttonRef.current?.getBoundingClientRect();
    if (!preventOpen.current && rect) {
      showFlyout({ position: { top: rect.bottom, left: rect.left }, ...flyoutOptions });
    }
  };

  const closeFlyout = () => {
    hideFlyout();
  };

  const insertTable = (nrOfRows: number, nrOfColumns: number) => {
    editor.update(() => {
      const selection = $getSelection();
      const tableNode = createTable(nrOfRows, nrOfColumns);
      if ($isRangeSelection(selection)) {
        selection.insertNodes([tableNode]);
      } else {
        $getRoot().append(tableNode);
      }
      const firstDescendant = tableNode.getFirstDescendant();
      if (firstDescendant) {
        (firstDescendant as TextNode).select();
      }
    });

    closeFlyout();
  };

  const mutateTable = (action: Actions) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        const anchorNode = selection.anchor.getNode();
        const currentNode = $getTableCellNodeFromLexicalNode(anchorNode);

        if ($isTableCellNode(currentNode)) {
          switch (action) {
            case 'insertRowAbove':
            case 'insertRowBelow':
              insertRow(currentNode, action);
              break;
            case 'deleteRow':
              deleteRow(currentNode);
              break;
            case 'insertColumnLeft':
            case 'insertColumnRight':
              insertColumn(currentNode, action);
              break;
            case 'deleteColumn':
              deleteColumn(currentNode);
              break;
            case 'deleteTable':
              deleteTable(currentNode);
              break;
          }
        }
      }
    });
  };

  const flyoutOptions = {
    children: (
      <AddTableForm
        onCloseHandler={closeFlyout}
        onSaveHandler={(rows, columns) => insertTable(rows, columns)}
        ref={addTableForm}
      />
    ),
  };

  useEffect(() => {
    if (!isVisible) {
      addTableForm.current?.clearForm();
      setTimeout(() => (preventOpen.current = false), PREVENT_OPEN_DELAY);
    } else {
      preventOpen.current = true;
      addTableForm.current?.focusFirstField();
    }
  }, [addTableForm, isVisible]);

  useEffect(() => {
    const removeKeyboardShortcut = editor.registerCommand(
      KEY_DOWN_COMMAND,
      (event: KeyboardEvent) => {
        if (event.ctrlKey && event.shiftKey && event.code === 'Digit9') {
          event.preventDefault();
          openFlyout();
          return true;
        }
        return false;
      },
      COMMAND_PRIORITY_EDITOR
    );

    return () => {
      removeKeyboardShortcut();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    isVisibleRef.current = isVisible;
  }, [isVisible]);

  useEffect(() => {
    const removeKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isVisibleRef.current) {
        event.preventDefault();
        event.stopPropagation();
        closeFlyout();
      }
    };

    document.addEventListener('keydown', removeKeydown, { capture: true });

    return () => {
      document.removeEventListener('keydown', removeKeydown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(ref, () => ({
    mutateTable,
  }));

  return (
    <div className={styles['editor__toolbar-button-group']}>
      <div className={styles['editor__toolbar-button-flyoutwrapper']}>
        <button
          onClick={openFlyout}
          title={t.table.tableTooltip}
          className={clsx(styles['editor__toolbar-button'], isActive && styles[`editor__toolbar-button--active`])}
          aria-label={t.aria.labels.table}
          aria-keyshortcuts={t.aria.keyshortcuts.table}
          ref={buttonRef}
          type="button"
        >
          <Icon name="Table" size={24} />
        </button>
        {flyout}
      </div>
    </div>
  );
});
